import { useGunsmithStore } from './store/gunsmithStore';

const SAVE_STATE_LABEL: Record<string, string> = {
  idle: '',
  saving: 'Working...',
  saved: 'All changes saved',
  error: 'Error',
};

/** Save/undo/redo/publish chrome above the workspace - the controls gunsmithStore's actions need, not just the state. */
export function GunsmithToolbar() {
  const weapons = useGunsmithStore((s) => s.weapons);
  const activeWeaponId = useGunsmithStore((s) => s.activeWeaponId);
  const sourceWeapons = useGunsmithStore((s) => s.sourceWeapons);
  const undoStack = useGunsmithStore((s) => s.undoStack);
  const redoStack = useGunsmithStore((s) => s.redoStack);
  const saveState = useGunsmithStore((s) => s.saveState);
  const statusMessage = useGunsmithStore((s) => s.statusMessage);
  const publishedWeaponIds = useGunsmithStore((s) => s.publishedWeaponIds);
  const undo = useGunsmithStore((s) => s.undo);
  const redo = useGunsmithStore((s) => s.redo);
  const saveActiveWeapon = useGunsmithStore((s) => s.saveActiveWeapon);
  const publishActiveWeapon = useGunsmithStore((s) => s.publishActiveWeapon);
  const resetActiveToSource = useGunsmithStore((s) => s.resetActiveToSource);

  const active = weapons.find((w) => w.id === activeWeaponId);
  const canUndo = active ? undoStack.some((e) => e.weaponId === active.id) : false;
  const canRedo = active ? redoStack.some((e) => e.weaponId === active.id) : false;
  const isSaved = active ? sourceWeapons.some((w) => w.id === active.id) : false;
  const isDirty = active ? !isSaved || undoStack.some((e) => e.weaponId === active.id) : false;
  const isPublished = active ? publishedWeaponIds.includes(active.id) : false;
  const busy = saveState === 'saving';

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
        borderBottom: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
        background: 'var(--ss-bg-2, #14171c)', flexShrink: 0,
      }}
    >
      <span style={{ fontFamily: 'var(--ss-display, inherit)', fontSize: 13.5, fontWeight: 500, color: 'var(--ss-text, #dfe3e8)' }}>
        {active ? active.name || 'Untitled weapon' : 'Gunsmith'}
      </span>
      {active && (
        <span
          title={isPublished ? 'Published' : 'Not published'}
          style={{
            fontSize: 9.5, fontWeight: 600, letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 999,
            color: isPublished ? 'var(--ss-success, #3fae6a)' : 'var(--ss-text-3, #6d747e)',
            border: '1px solid ' + (isPublished ? 'var(--ss-success, #3fae6a)' : 'var(--ss-border-soft, rgba(255,255,255,0.08))'),
          }}
        >
          {isPublished ? 'PUBLISHED' : 'DRAFT'}
        </span>
      )}

      <div style={{ flex: 1 }} />

      {statusMessage && (
        <span style={{ fontSize: 11.5, color: saveState === 'error' ? 'var(--ss-danger, #d0453f)' : 'var(--ss-text-3, #6d747e)' }}>
          {statusMessage}
        </span>
      )}
      {!statusMessage && SAVE_STATE_LABEL[saveState] && (
        <span style={{ fontSize: 11.5, color: 'var(--ss-text-3, #6d747e)' }}>{SAVE_STATE_LABEL[saveState]}</span>
      )}

      <button type="button" className="ss-btn ss-btn--ghost ss-btn--sm" onClick={undo} disabled={!canUndo} title="Undo (last change to this weapon)">
        ↶ Undo
      </button>
      <button type="button" className="ss-btn ss-btn--ghost ss-btn--sm" onClick={redo} disabled={!canRedo} title="Redo">
        ↷ Redo
      </button>
      <button
        type="button"
        className="ss-btn ss-btn--ghost ss-btn--sm"
        onClick={resetActiveToSource}
        disabled={!active || !isSaved || !isDirty}
        title={isSaved ? 'Discard unsaved changes, back to the last save' : 'Nothing saved yet to revert to'}
      >
        Revert
      </button>
      <button
        type="button"
        className="ss-btn ss-btn--sm"
        onClick={() => void saveActiveWeapon()}
        disabled={!active || busy || !isDirty}
      >
        Save
      </button>
      <button
        type="button"
        className="ss-btn ss-btn--primary ss-btn--sm"
        onClick={() => void publishActiveWeapon()}
        disabled={!active || busy}
        title="Save, then publish"
      >
        Publish
      </button>
    </div>
  );
}
